import { useState } from 'react';
import { IconLock, IconSunHigh, IconAlertTriangle } from '@tabler/icons-react';
import { useDaySession } from '../hooks/useDaySession';
import { useRole } from '../hooks/useRole';
import { OpenDayModal } from './DaySessionModals';
import DayClosedScreen from './DayClosedScreen';

/**
 * DayNotOpenOverlay (Phase 1.6b — redesigned).
 *
 * Owner hard rule: "cashiers can only ring orders while a day session
 * is OPEN. If no supervisor has opened the day, the POS shows 'Day
 * not yet open — find your supervisor'."
 *
 * Visual treatment: **system-lockout panel**, not a soft warning. The
 * till is hard-locked until a supervisor acts. Black panel, red lock
 * + red urgency eyebrow, white headline, green confirm CTA (the only
 * positive action) for supervisors. Cashiers see no button — just the
 * directive.
 */
export default function DayNotOpenOverlay() {
  const { current, closedToday, loading } = useDaySession();
  const { hasMinRole } = useRole();
  const [openDayModalOpen, setOpenDayModalOpen] = useState(false);

  if (loading) return null;
  if (current) return null;

  // Phase 4.6 — if the day was already closed today (SAST), show the
  // celebratory post-close screen instead. Locks for everyone — no
  // re-open button, not even for supervisor.
  if (closedToday) {
    return <DayClosedScreen closedToday={closedToday} />;
  }

  const isSupervisor = hasMinRole('SUPERVISOR');

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/75 backdrop-blur-md flex items-center justify-center px-6">
        <div className="
          relative w-full max-w-md rounded-2xl overflow-hidden animate-slide-up
          bg-[#0A0A0A] border-2 border-[#1F1F1F]
          shadow-[0_25px_80px_-20px_rgba(221,0,0,0.4),0_8px_24px_rgba(0,0,0,0.6)]
        ">
          {/* Top red accent stripe — signals system alert */}
          <div className="h-1 bg-gradient-to-r from-[#7A0303] via-[#DD0000] to-[#7A0303]" />

          <div className="p-9 text-center">
            {/* Icon — red lock on red-tinted halo */}
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full bg-[#DD0000]/15 blur-xl" />
              <div className="relative w-20 h-20 rounded-2xl bg-[#1A0303] border-2 border-[#DD0000]/40 inline-flex items-center justify-center shadow-lg">
                <IconLock size={36} stroke={2.25} className="text-[#FF3333]" />
              </div>
            </div>

            {/* Urgency eyebrow — red, uppercase, tracked */}
            <p className="inline-flex items-center gap-2 text-12 font-bold uppercase tracking-[0.3em] text-[#FF4444] mb-4">
              <IconAlertTriangle size={14} stroke={2.5} fill="currentColor" className="text-[#FF4444]" />
              Day not yet open
            </p>

            {/* Headline — pure white */}
            <h2 className="text-26 font-bold text-[#FFFFFF] leading-tight mb-3">
              Trading hasn't started
            </h2>

            {/* Body — light grey */}
            <p className="text-14 font-medium text-[#B8BCC2] leading-relaxed mb-7 max-w-[340px] mx-auto">
              {isSupervisor
                ? 'Open the trading day to unlock the till. You\'ll declare your opening cash float.'
                : 'A supervisor must open the day before you can ring orders. Please find your supervisor.'}
            </p>

            {/* CTA — green for supervisors (positive action), nothing for cashiers */}
            {isSupervisor ? (
              <button
                type="button"
                onClick={() => setOpenDayModalOpen(true)}
                className="w-full h-13 inline-flex items-center justify-center gap-2.5 rounded-xl bg-success text-[#FFFFFF] text-15 font-bold hover:bg-success-hover shadow-[0_8px_24px_-8px_rgba(20,181,29,0.6)] transition-all active:scale-[0.99]"
                style={{ height: '52px' }}
              >
                <IconSunHigh size={20} stroke={2.5} />
                Open day &amp; start trading
              </button>
            ) : (
              <div className="border-t border-[#1F1F1F] pt-4 mt-2">
                <p className="text-11 font-bold uppercase tracking-widest text-[#FF4444]">
                  Locked
                </p>
                <p className="text-13 font-medium text-[#7A7E84] mt-1">
                  Waiting for supervisor authorisation
                </p>
              </div>
            )}
          </div>

          {/* Bottom red accent stripe */}
          <div className="h-1 bg-gradient-to-r from-[#7A0303] via-[#DD0000] to-[#7A0303]" />
        </div>
      </div>

      <OpenDayModal
        open={openDayModalOpen}
        onClose={() => setOpenDayModalOpen(false)}
      />
    </>
  );
}
